// Public API: suggestTablesInScope(scope, snapshot, filter?) → Table[].
// Pure function. Returns the tables/views/CTEs reachable at the cursor's
// position, in deterministic order: in-scope CTEs first, then current-schema
// snapshot tables, then other-schema snapshot tables. Oracle's `DUAL`
// pseudo-table is always included.
//
// Excludes pseudo-tables (`excluded`, `inserted`/`deleted`, `:NEW`/`:OLD`,
// `NEW`/`OLD`) — those are scope-local and only legal via dotted access,
// never bare FROM. Snapshot.tables shouldn't carry them; we additionally
// guard against accidental inclusion via reachableTables.via === 'pseudo'
// not being looked at here at all (we go straight to snapshot.tables).
//
// Snapshot is threaded as the second positional arg, mirroring
// suggestColumnsInScope — Scope is pure lexical/structural info and never
// carries a SchemaSnapshot reference. (See PROGRESS.md Codebase Patterns.)
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Suggestion — Tables
// in Scope.

import type { SqlEngine } from '../../sql-grammar/model';
import { foldIdentifier } from '../scope/identifiers';
import { relationKey } from '../service/snapshotRelations';
import type { CteBinding, SchemaSnapshot, Scope, Table } from '../types';

export function suggestTablesInScope(
  scope: Scope,
  snapshot: SchemaSnapshot | undefined,
  filter?: string,
): Table[] {
  const filterLower = filter ? filter.toLowerCase() : undefined;
  const matches = (name: string): boolean =>
    !filterLower || name.toLowerCase().startsWith(filterLower);

  const out: Table[] = [];
  const seen = new Set<string>(); // dedup key: `${kind}|${relationKey}` (injective)

  // 1. CTEs visible at this scope (innermost-first across the parent chain).
  //    Innermost wins on dedup; later (outer) duplicates are skipped.
  for (const cte of walkScopeCtes(scope)) {
    const key = `cte||${cte.name.foldedName}`;
    if (seen.has(key)) continue;
    if (!matches(cte.name.name)) continue;
    seen.add(key);
    out.push({ qualified: cte.name, kind: 'cte' });
  }

  // 2. Snapshot tables — current-schema bucket, then other-schema bucket.
  //    Within each bucket, sort by foldedSchema then foldedName for stability.
  if (snapshot) {
    // Fold currentSchema using the same engine rule that produced foldedSchema
    // on each table (Oracle = upper, others = lower/case-insensitive). This
    // ensures a fold-vs-fold comparison rather than a naive .toLowerCase() on
    // both sides (which would invert Oracle's UPPER fold back to lower).
    const engineOpts = { engine: snapshot.engineId as SqlEngine };
    const currentFolded = scope.currentSchema !== undefined
      ? foldIdentifier(scope.currentSchema, false, engineOpts)
      : undefined;
    const currentBucket: Table[] = [];
    const otherBucket: Table[] = [];
    for (const t of snapshot.tables.values()) {
      if (!matches(t.qualified.name)) continue;
      const key = `t|${relationKey(t.qualified)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      // Compare already-folded schema directly (no extra .toLowerCase()).
      const tableSchema = t.qualified.foldedSchema;
      // Unqualified or matching-schema tables go in the current bucket;
      // anything else (different schema) lands in other.
      if (!tableSchema || (currentFolded && tableSchema === currentFolded)) {
        currentBucket.push(t);
      } else {
        otherBucket.push(t);
      }
    }
    currentBucket.sort(byQualifiedNameOrder);
    otherBucket.sort(byQualifiedNameOrder);
    out.push(...currentBucket, ...otherBucket);
  }

  // 3. Oracle `DUAL` is always callable. Add it only if not already present
  //    via the snapshot (some Oracle introspection paths surface SYS.DUAL).
  if (snapshot?.engineId === 'oracle' && matches('DUAL')) {
    const dual = {
      name: 'DUAL',
      raw: 'DUAL',
      quoted: false,
      foldedName: 'DUAL',
      schema: 'SYS',
      foldedSchema: 'SYS',
    };
    const dualKey = `t|${relationKey(dual)}`;
    if (!seen.has(dualKey)) {
      seen.add(dualKey);
      out.push({ qualified: dual, kind: 'table' });
    }
  }

  return out;
}

// Walk the scope chain innermost-first, yielding every CteBinding.
function* walkScopeCtes(scope: Scope): Generator<CteBinding> {
  let s: Scope | undefined = scope;
  while (s) {
    for (const c of s.ctes) yield c;
    s = s.parent;
  }
}

// Within a bucket: sort by (foldedSchema, foldedName) lexicographically.
function byQualifiedNameOrder(a: Table, b: Table): number {
  const as = a.qualified.foldedSchema ?? '';
  const bs = b.qualified.foldedSchema ?? '';
  if (as < bs) return -1;
  if (as > bs) return 1;
  const an = a.qualified.foldedName;
  const bn = b.qualified.foldedName;
  if (an < bn) return -1;
  if (an > bn) return 1;
  return 0;
}
